import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatCurrency, formatDate, API_URL, getAuthHeaders, parseISODate, formatLocalISO } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Plus,
  Trash2,
  Edit,
  ArrowLeft,
  CalendarIcon,
  Wallet,
  Upload,
  Image,
  Tag,
  Building2,
  Loader2,
  Search,
  X,
  Sparkles,
  ExternalLink,
  Mail,
  Check,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { Switch } from "../components/ui/switch";

// Receipts imported from the billing inbox can be PDFs or plain text, which an <img> cannot show.
const isImageReceipt = (url) => /\.(jpe?g|png|gif|webp)$/i.test(url || "");

const openReceipt = async (url) => {
  try {
    const resp = await fetch(`${API_URL}${url.replace(/^\/api/, "")}`, { headers: getAuthHeaders() });
    if (!resp.ok) throw new Error(`Could not load receipt (${resp.status})`);
    const blob = await resp.blob();
    window.open(URL.createObjectURL(blob), "_blank", "noopener");
  } catch (err) {
    toast.error(err.message || "Could not load receipt");
  }
};

// Reusable spend gauge that polls /api/settings/gemini/usage.
// `refreshKey` lets parents trigger a re-fetch after a scan completes.
const GeminiSpendGauge = ({ refreshKey = 0, compact = false }) => {
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/gemini/usage")
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch((err) => { if (!cancelled) setError(err.message || "Could not load usage"); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (error || !usage) {
    return null;
  }

  const pct = Math.min(usage.percent_used, 100);
  const barColor =
    usage.percent_used >= 100 ? "bg-destructive"
    : usage.percent_used >= 75 ? "bg-amber-500"
    : "bg-primary";

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Sparkles className="w-3 h-3 text-primary" />
        <span>${usage.cost_usd.toFixed(4)} / ${usage.cap_usd.toFixed(2)}</span>
        <div className="h-1.5 w-24 rounded-full bg-accent/40 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">AI scans this month ({usage.month})</span>
          </div>
          <a
            href="https://aistudio.google.com/usage"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            Google usage <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold">${usage.cost_usd.toFixed(4)} / ${usage.cap_usd.toFixed(2)}</span>
          <span className="text-muted-foreground">{usage.scan_count} {usage.scan_count === 1 ? "scan" : "scans"} · {usage.percent_used.toFixed(1)}%</span>
        </div>
        <div className="h-2 rounded-full bg-accent/40 overflow-hidden">
          <div className={`h-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        {usage.percent_used >= 100 && (
          <p className="text-xs text-destructive">
            Over cap — {usage.over_cap_behavior === "block" ? "scans paused until next month" : "scans still working; bump the cap in Settings → AI if needed"}.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Expenses List Page
export const ExpensesPage = () => {
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [reviewOnly, setReviewOnly] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: "", color: "#000080" });
  const [newVendor, setNewVendor] = useState({ name: "", email: "", phone: "", address: "" });
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [monthlyDialogOpen, setMonthlyDialogOpen] = useState(false);
  const [monthlySettings, setMonthlySettings] = useState(null);
  const [monthlyForm, setMonthlyForm] = useState({
    date: formatLocalISO(new Date()),
    rent_amount: 0,
    utilities_total: 0,
  });
  const [monthlySubmitting, setMonthlySubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesData, categoriesData, vendorsData] = await Promise.all([
        api.get("/expenses"),
        api.get("/categories"),
        api.get("/vendors"),
      ]);
      setExpenses(expensesData);
      setCategories(categoriesData);
      setVendors(vendorsData);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error("Please enter a category name");
      return;
    }
    try {
      await api.post("/categories", newCategory);
      toast.success("Category created");
      setNewCategory({ name: "", color: "#000080" });
      setCategoryDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Category deleted");
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddVendor = async () => {
    if (!newVendor.name) {
      toast.error("Please enter a vendor name");
      return;
    }
    try {
      await api.post("/vendors", newVendor);
      toast.success("Vendor created");
      setNewVendor({ name: "", email: "", phone: "", address: "" });
      setVendorDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteVendor = async (id) => {
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendor deleted");
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleOpenMonthlyDialog = async () => {
    setMonthlyDialogOpen(true);
    try {
      const settings = await api.get("/settings/rent-utilities");
      setMonthlySettings(settings);
      setMonthlyForm({
        date: formatLocalISO(new Date()),
        rent_amount: settings.default_rent_amount || 0,
        utilities_total: 0,
      });
    } catch (err) {
      toast.error("Failed to load Rent + Utilities settings");
    }
  };

  const handleRecordMonthlyExpenses = async () => {
    if (!monthlyForm.date || monthlyForm.rent_amount < 0 || monthlyForm.utilities_total < 0) {
      toast.error("Please fill in valid values");
      return;
    }
    setMonthlySubmitting(true);
    try {
      await api.post("/expenses/record-monthly", monthlyForm);
      toast.success("Monthly expenses recorded");
      setMonthlyDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMonthlySubmitting(false);
    }
  };

  const utilityPercentage = monthlySettings?.utility_percentage ?? 25;
  const utilitiesExpensed = (monthlyForm.utilities_total || 0) * (utilityPercentage / 100);

  const needsReviewCount = expenses.filter((exp) => exp.review_status === "needs_review").length;

  const filteredExpenses = expenses.filter((exp) => {
    const matchesSearch = exp.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || exp.category_id === categoryFilter;
    const matchesReview = !reviewOnly || exp.review_status === "needs_review";
    return matchesSearch && matchesCategory && matchesReview;
  });

  const handleApprove = async (expense) => {
    try {
      await api.post(`/expenses/${expense.id}/approve`, {});
      toast.success("Expense approved");
      fetchData();
    } catch (err) {
      toast.error(err.message || "Approve failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading">Expenses</h1>
          <p className="text-muted-foreground mt-1">Track and manage your business expenses</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="manage-categories-btn">
                  <Tag className="w-4 h-4 mr-2" /> Categories
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl">
                <DialogHeader>
                  <DialogTitle>Manage Categories</DialogTitle>
                  <DialogDescription>Add or remove expense categories</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Category name"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      className="rounded-xl"
                      data-testid="new-category-name"
                    />
                    <Input
                      type="color"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                      className="w-14 h-10 rounded-xl p-1"
                    />
                    <Button onClick={handleAddCategory} className="rounded-full" data-testid="add-category-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {categories.map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between p-2 rounded-xl bg-accent/30">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span>{cat.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="rounded-full text-destructive h-8 w-8"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="manage-vendors-btn">
                  <Building2 className="w-4 h-4 mr-2" /> Vendors
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl">
                <DialogHeader>
                  <DialogTitle>Manage Vendors</DialogTitle>
                  <DialogDescription>Add or remove vendors</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <Input
                      placeholder="Vendor name"
                      value={newVendor.name}
                      onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                      className="rounded-xl"
                      data-testid="new-vendor-name"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Email"
                        value={newVendor.email}
                        onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                        className="rounded-xl"
                      />
                      <Input
                        placeholder="Phone"
                        value={newVendor.phone}
                        onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                    <Button onClick={handleAddVendor} className="w-full rounded-full" data-testid="add-vendor-btn">
                      <Plus className="w-4 h-4 mr-2" /> Add Vendor
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {vendors.map((vendor) => (
                      <div key={vendor.id} className="flex items-center justify-between p-3 rounded-xl bg-accent/30">
                        <div>
                          <p className="font-medium">{vendor.name}</p>
                          {vendor.email && <p className="text-sm text-muted-foreground">{vendor.email}</p>}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteVendor(vendor.id)}
                          className="rounded-full text-destructive h-8 w-8"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={monthlyDialogOpen} onOpenChange={setMonthlyDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={handleOpenMonthlyDialog}
                  data-testid="record-monthly-expenses-btn"
                >
                  <Wallet className="w-4 h-4 mr-2" /> Record Monthly Expenses
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl">
                <DialogHeader>
                  <DialogTitle>Record Monthly Expenses</DialogTitle>
                  <DialogDescription>
                    Records your rent write-off, utilities portion, internet, and phone as expenses under "Rent + Utilities"
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start rounded-xl" data-testid="monthly-date">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {monthlyForm.date ? format(parseISODate(monthlyForm.date), "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-xl">
                        <Calendar
                          mode="single"
                          selected={parseISODate(monthlyForm.date)}
                          onSelect={(date) =>
                            setMonthlyForm({ ...monthlyForm, date: formatLocalISO(date) })
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Rent Portion ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={monthlyForm.rent_amount}
                      onChange={(e) =>
                        setMonthlyForm({ ...monthlyForm, rent_amount: parseFloat(e.target.value) || 0 })
                      }
                      className="rounded-xl"
                      data-testid="monthly-rent-amount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Utilities This Month ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={monthlyForm.utilities_total}
                      onChange={(e) =>
                        setMonthlyForm({ ...monthlyForm, utilities_total: parseFloat(e.target.value) || 0 })
                      }
                      className="rounded-xl"
                      data-testid="monthly-utilities-total"
                    />
                  </div>
                  <div className="space-y-1 p-3 rounded-xl bg-accent/30 text-sm">
                    <p>
                      Utilities expensed: {utilityPercentage}% of {formatCurrency(monthlyForm.utilities_total || 0)} ={" "}
                      <span className="font-medium">{formatCurrency(utilitiesExpensed)}</span>
                    </p>
                    <p>Internet: {formatCurrency(monthlySettings?.internet_amount ?? 25)}</p>
                    <p>Phone: {formatCurrency(monthlySettings?.phone_amount ?? 10)}</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleRecordMonthlyExpenses}
                    disabled={monthlySubmitting}
                    className="rounded-full"
                    data-testid="submit-monthly-expenses"
                  >
                    {monthlySubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Record Expenses
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button
              onClick={() => navigate("/expenses/new")}
              className="rounded-full shadow-glow-magenta bg-secondary"
              data-testid="new-expense-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Expense
            </Button>
          </div>
        )}
      </div>

      <GeminiSpendGauge />

      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-xl"
                data-testid="search-expenses"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px] rounded-xl" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(needsReviewCount > 0 || reviewOnly) && (
              <Button
                type="button"
                variant={reviewOnly ? "default" : "outline"}
                onClick={() => setReviewOnly((v) => !v)}
                className="rounded-xl"
                data-testid="needs-review-filter"
              >
                <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
                Needs review ({needsReviewCount})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No expenses found</p>
              {canEdit && (
                <Button
                  variant="outline"
                  onClick={() => navigate("/expenses/new")}
                  className="mt-4 rounded-full"
                >
                  Add your first expense
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => (
                    <TableRow key={expense.id} className="cursor-pointer hover:bg-accent/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {expense.receipt_url && <Image className="w-4 h-4 text-primary" />}
                          {expense.source === "email" && (
                            <span title={`From billing inbox: ${expense.ingest_subject || ""}`}><Mail className="w-4 h-4 text-muted-foreground" /></span>
                          )}
                          <span className="font-medium">{expense.description}</span>
                          {expense.review_status === "needs_review" && (
                            <span
                              className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 text-xs font-medium whitespace-nowrap"
                              title={(expense.review_reasons || []).join("; ")}
                              data-testid={`review-badge-${expense.id}`}
                            >
                              Needs review
                            </span>
                          )}
                        </div>
                        {expense.review_status === "needs_review" && (expense.review_reasons || []).length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">{expense.review_reasons.join(" · ")}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor: categories.find((c) => c.id === expense.category_id)?.color || "#666",
                            }}
                          />
                          {expense.category_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {expense.vendor_name || "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(expense.date)}</TableCell>
                      <TableCell className="text-right font-mono text-destructive">
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {canEdit && expense.review_status === "needs_review" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleApprove(expense)}
                            className="rounded-full text-primary"
                            title="Approve"
                            data-testid={`approve-expense-${expense.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/expenses/${expense.id}/edit`)}
                            className="rounded-full"
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Expense Form Component
const ExpenseForm = ({ initialData, categories, vendors, onSubmit, loading }) => {
  const [formData, setFormData] = useState(
    initialData || {
      description: "",
      amount: "",
      category_id: "",
      vendor_id: "",
      date: formatLocalISO(new Date()),
      notes: "",
    }
  );
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(initialData?.receipt_url || null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [scanEnabled, setScanEnabled] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null); // { cost_usd, confidence }
  const [gaugeKey, setGaugeKey] = useState(0);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));

    // If AI scan is enabled and this is a CREATE (not an edit with existing data),
    // send the image to Gemini and auto-populate the form.
    if (scanEnabled && !initialData) {
      setScanning(true);
      setLastScan(null);
      const start = Date.now();
      try {
        const fd = new FormData();
        fd.append("file", file);
        const resp = await fetch(`${API_URL}/expenses/scan-receipt`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: fd,
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.detail || `Scan failed (${resp.status})`);
        }
        const data = await resp.json();
        const d = data.draft || {};
        setFormData((prev) => ({
          description: d.description || prev.description,
          amount: d.amount || prev.amount,
          category_id: d.category_id || prev.category_id,
          vendor_id: d.vendor_id || prev.vendor_id,
          date: d.date || prev.date,
          notes: d.notes || prev.notes,
        }));
        setLastScan({
          cost_usd: data.cost_usd || 0,
          confidence: data.extraction?.confidence || "medium",
          elapsed_ms: Date.now() - start,
        });
        setGaugeKey((k) => k + 1);
        toast.success(`Scanned in ${((Date.now() - start) / 1000).toFixed(1)}s`);
      } catch (err) {
        toast.error(err.message || "Scan failed — form stays manual");
      } finally {
        setScanning(false);
      }
    }
  };

  // Set by the "Save & approve" button just before it submits, so both buttons share validation.
  const approveOnSaveRef = useRef(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const approve = approveOnSaveRef.current;
    approveOnSaveRef.current = false;
    if (!formData.description || !formData.category_id || !formData.date) {
      toast.error("Please fill in required fields");
      return;
    }
    if (formData.amount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    onSubmit(formData, receiptFile, { approve });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {initialData?.review_status === "needs_review" && (
        <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-sm space-y-1" data-testid="review-banner">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Imported from the billing inbox and waiting for review
          </div>
          {(initialData.review_reasons || []).length > 0 && (
            <p className="text-muted-foreground">Why: {initialData.review_reasons.join(" · ")}</p>
          )}
          {initialData.ingest_subject && (
            <p className="text-muted-foreground">Email: {initialData.ingest_subject} ({initialData.ingest_from})</p>
          )}
          <p className="text-muted-foreground">Fix anything that is wrong, then use <span className="font-medium">Save &amp; approve</span> below. It is left out of reports until approved.</p>
        </div>
      )}
      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Expense Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Description *</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="rounded-xl"
              placeholder="Office supplies, Travel, etc."
              data-testid="expense-description"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                className="rounded-xl"
                placeholder="0.00"
                data-testid="expense-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start rounded-xl" data-testid="expense-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date ? format(parseISODate(formData.date), "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-xl">
                  <Calendar
                    mode="single"
                    selected={parseISODate(formData.date)}
                    onSelect={(date) =>
                      setFormData({ ...formData, date: formatLocalISO(date) })
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="expense-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Select
                value={formData.vendor_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, vendor_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="expense-vendor">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="none">No vendor</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="rounded-xl"
              placeholder="Additional details..."
              data-testid="expense-notes"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Receipt</CardTitle>
              {!initialData && (
                <p className="text-xs text-muted-foreground mt-1">
                  {scanEnabled
                    ? "Drop an image and Gemini auto-fills the fields above."
                    : "Upload will attach the image only — fields stay manual."}
                </p>
              )}
            </div>
            {!initialData && (
              <div className="flex items-center gap-2 shrink-0">
                <Sparkles className={`w-4 h-4 ${scanEnabled ? "text-primary" : "text-muted-foreground"}`} />
                <Label htmlFor="scan-toggle" className="text-sm cursor-pointer select-none">Scan with AI</Label>
                <Switch
                  id="scan-toggle"
                  checked={scanEnabled}
                  onCheckedChange={setScanEnabled}
                  data-testid="scan-toggle"
                />
              </div>
            )}
          </div>
          {!initialData && <GeminiSpendGauge refreshKey={gaugeKey} compact />}
        </CardHeader>
        <CardContent>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            data-testid="receipt-input"
          />
          {/* Second input opens the device camera directly on mobile; both feed
              the same handleFileChange (10MB check + AI scan preserved). */}
          <input
            type="file"
            ref={cameraInputRef}
            onChange={handleFileChange}
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid="receipt-camera-input"
          />

          {receiptPreview && !receiptPreview.startsWith("blob:") && !isImageReceipt(receiptPreview) ? (
            <div className="win-well p-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-5 h-5 text-primary" />
                {/\.pdf$/i.test(receiptPreview) ? "PDF receipt attached" : "Emailed receipt attached"}
              </div>
              <Button type="button" variant="outline" onClick={() => openReceipt(receiptPreview)} data-testid="view-receipt">
                View receipt
              </Button>
            </div>
          ) : receiptPreview ? (
            <div className="relative">
              <img
                src={receiptPreview.startsWith("blob:") ? receiptPreview : `${API_URL}${receiptPreview}`}
                alt="Receipt preview"
                className={`w-full max-h-64 object-contain rounded-xl transition-opacity ${scanning ? "opacity-50" : ""}`}
              />
              {scanning && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex items-center gap-2 px-4 py-2 win-raised">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm">Reading receipt...</span>
                  </div>
                </div>
              )}
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 rounded-full"
                disabled={scanning}
                onClick={() => {
                  setReceiptFile(null);
                  setReceiptPreview(null);
                  setLastScan(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
              {lastScan && !scanning && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Sparkles className="w-3 h-3 text-primary" />
                  <span>Scanned in {(lastScan.elapsed_ms / 1000).toFixed(1)}s · cost ~${lastScan.cost_usd.toFixed(4)} · {lastScan.confidence} confidence</span>
                </div>
              )}
            </div>
          ) : (
            <div className="win-well p-8 text-center">
              {scanEnabled && !initialData ? (
                <Sparkles className="w-8 h-8 mx-auto text-primary mb-3" />
              ) : (
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
              )}
              <p className="text-muted-foreground mb-4">
                {scanEnabled && !initialData ? "Add a receipt to scan with AI" : "Add a receipt"}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-2">
                <Button type="button" onClick={() => cameraInputRef.current?.click()}>
                  📷 Take Photo
                </Button>
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                  📁 Choose File
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Max 10MB - JPEG, PNG, GIF, WEBP</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        {initialData?.review_status === "needs_review" && (
          <Button
            type="submit"
            disabled={loading}
            variant="outline"
            className="rounded-full"
            onClick={() => { approveOnSaveRef.current = true; }}
            data-testid="save-approve-expense"
          >
            <Check className="w-4 h-4 mr-2" />
            Save &amp; approve
          </Button>
        )}
        <Button type="submit" disabled={loading} className="rounded-full shadow-glow-magenta bg-secondary" data-testid="save-expense">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save Expense
        </Button>
      </div>
    </form>
  );
};

// Create Expense Page
export const CreateExpensePage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesData, vendorsData] = await Promise.all([
        api.get("/categories"),
        api.get("/vendors"),
      ]);
      setCategories(categoriesData);
      setVendors(vendorsData);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data, receiptFile) => {
    setSaving(true);
    try {
      const expense = await api.post("/expenses", data);
      
      if (receiptFile) {
        await api.upload(`/expenses/${expense.id}/upload-receipt`, receiptFile);
      }
      
      toast.success("Expense created successfully");
      navigate("/expenses");
    } catch (err) {
      toast.error(err.message || "Failed to create expense");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="create-expense-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/expenses")} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-heading">New Expense</h1>
          <p className="text-muted-foreground mt-1">Record a new business expense</p>
        </div>
      </div>

      {categories.length === 0 ? (
        <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
          <CardContent className="p-12 text-center">
            <Tag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">Please create at least one category first</p>
            <Button onClick={() => navigate("/expenses")} className="rounded-full">
              Go to Expenses
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ExpenseForm
          categories={categories}
          vendors={vendors}
          onSubmit={handleSubmit}
          loading={saving}
        />
      )}
    </div>
  );
};

// Edit Expense Page
export const EditExpensePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [expense, setExpense] = useState(null);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [expenseData, categoriesData, vendorsData] = await Promise.all([
        api.get(`/expenses/${id}`),
        api.get("/categories"),
        api.get("/vendors"),
      ]);
      setExpense(expenseData);
      setCategories(categoriesData);
      setVendors(vendorsData);
    } catch (err) {
      toast.error("Failed to load data");
      navigate("/expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data, receiptFile, opts = {}) => {
    setSaving(true);
    try {
      await api.put(`/expenses/${id}`, data);
      
      if (receiptFile) {
        await api.upload(`/expenses/${id}/upload-receipt`, receiptFile);
      }

      if (opts.approve) {
        await api.post(`/expenses/${id}/approve`, {});
      }
      
      toast.success(opts.approve ? "Expense saved and approved" : "Expense updated successfully");
      navigate("/expenses");
    } catch (err) {
      toast.error(err.message || "Failed to update expense");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      navigate("/expenses");
    } catch (err) {
      toast.error(err.message || "Failed to delete expense");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="edit-expense-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/expenses")} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-heading">Edit Expense</h1>
            <p className="text-muted-foreground mt-1">{expense?.description}</p>
          </div>
        </div>
        <Button
          variant="destructive"
          onClick={() => setDeleteOpen(true)}
          className="rounded-full"
          data-testid="delete-expense-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </Button>
      </div>

      <ExpenseForm
        initialData={expense}
        categories={categories}
        vendors={vendors}
        onSubmit={handleSubmit}
        loading={saving}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Delete Expense</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-full">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
